/**
 * Search Index for v2v2 SWFL Farmers Market Application
 * Loads and preprocesses all product, vendor, and market data for unified search
 */

import { 
  SearchIndexEntry, 
  SearchResult, 
  SearchFilters, 
  SearchOptions,
  SEASON_MONTHS
} from '@/types/search';

// Data types from the JSON files
interface ProductData {
  id: string;
  name: string;
  slug: string;
  category?: string;
  subcategory?: string;
  description: string;
  long_description?: string;
  keywords?: string[];
  tags?: string[];
  seasonal_availability?: {
    primary_season?: {
      months?: string[];
      peak?: string[];
    };
    off_season?: {
      months?: string[];
    };
  };
  source_farms?: Array<{
    farm_id: string;
    farm_name: string;
    location?: string;
  }>;
}

interface VendorData {
  id: string;
  name: string;
  slug: string;
  type?: string;
  subtype?: string;
  description?: string;
  long_description?: string;
  vendorType?: string[];
  location?: {
    city?: string;
    county?: string;
    address?: string;
  };
  products?: string[];
  productCategories?: string[];
  specialities?: string[];
  tags?: string[];
}

interface MarketData {
  id: string;
  name: string;
  slug: string;
  shortName?: string;
  description?: string;
  longDescription?: string;
  categories?: string[];
  location?: {
    city?: string;
    county?: string;
    address?: string;
    neighborhood?: string;
  };
  tags?: string[];
  highlights?: string[];
}

// In-memory search index
let searchIndex: SearchIndexEntry[] = [];
let isIndexBuilt = false;
let indexPromise: Promise<void> | null = null;

// Normalize text for search
function normalizeText(text: string | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Extract searchable text from a value
function extractSearchableText(value: unknown): string {
  if (typeof value === 'string') {
    return normalizeText(value);
  }
  if (Array.isArray(value)) {
    return value.map(v => extractSearchableText(v)).join(' ');
  }
  if (typeof value === 'object' && value !== null) {
    return Object.values(value as Record<string, unknown>)
      .map(v => extractSearchableText(v))
      .join(' ');
  }
  return '';
}

// Build searchable text from multiple fields
function buildSearchableText(fields: Record<string, unknown>): string {
  const textParts: string[] = [];
  
  if (fields.name) textParts.push(extractSearchableText(fields.name));
  if (fields.description) textParts.push(extractSearchableText(fields.description));
  if (fields.longDescription) textParts.push(extractSearchableText(fields.longDescription));
  if (fields.category) textParts.push(extractSearchableText(fields.category));
  if (fields.subcategory) textParts.push(extractSearchableText(fields.subcategory));
  if (fields.tags) textParts.push(extractSearchableText(fields.tags));
  if (fields.keywords) textParts.push(extractSearchableText(fields.keywords));
  if (fields.specialities) textParts.push(extractSearchableText(fields.specialities));
  if (fields.productCategories) textParts.push(extractSearchableText(fields.productCategories));
  if (fields.vendorType) textParts.push(extractSearchableText(fields.vendorType));
  if (fields.location) textParts.push(extractSearchableText(fields.location));
  if (fields.city) textParts.push(extractSearchableText(fields.city));
  if (fields.county) textParts.push(extractSearchableText(fields.county));
  
  return textParts.join(' ');
}

// Create search index entry from product
function createProductEntry(product: ProductData): SearchIndexEntry {
  const seasonalMonths: string[] = [];
  
  if (product.seasonal_availability?.primary_season?.months) {
    seasonalMonths.push(...product.seasonal_availability.primary_season.months);
  }
  if (product.seasonal_availability?.off_season?.months) {
    seasonalMonths.push(...product.seasonal_availability.off_season.months);
  }
  
  const vendorIds = product.source_farms?.map(farm => farm.farm_id) || [];
  
  return {
    type: 'product',
    id: product.id,
    name: product.name,
    slug: product.slug,
    description: product.description || product.long_description || '',
    searchableText: buildSearchableText({
      name: product.name,
      description: product.description,
      longDescription: product.long_description,
      category: product.category,
      subcategory: product.subcategory,
      tags: product.tags,
      keywords: product.keywords,
    }),
    category: product.category,
    subcategory: product.subcategory,
    tags: product.tags,
    seasonalMonths: seasonalMonths.length > 0 ? seasonalMonths : undefined,
    vendorIds: vendorIds.length > 0 ? vendorIds : undefined,
  };
}

// Create search index entry from vendor
function createVendorEntry(vendor: VendorData): SearchIndexEntry {
  const vendorType = vendor.vendorType || (vendor.type ? [vendor.type] : undefined);
  
  return {
    type: 'vendor',
    id: vendor.id,
    name: vendor.name,
    slug: vendor.slug,
    description: vendor.description || vendor.long_description || '',
    searchableText: buildSearchableText({
      name: vendor.name,
      description: vendor.description,
      longDescription: vendor.long_description,
      vendorType: vendor.vendorType,
      productCategories: vendor.productCategories,
      specialities: vendor.specialities,
      tags: vendor.tags,
      location: vendor.location,
      city: vendor.location?.city,
      county: vendor.location?.county,
    }),
    category: vendor.productCategories?.[0],
    tags: vendor.tags,
    vendorType,
    location: vendor.location,
  };
}

// Create search index entry from market
function createMarketEntry(market: MarketData): SearchIndexEntry {
  return {
    type: 'market',
    id: market.id,
    name: market.name,
    slug: market.slug,
    description: market.description || market.longDescription || '',
    searchableText: buildSearchableText({
      name: market.name,
      description: market.description,
      longDescription: market.longDescription,
      categories: market.categories,
      location: market.location,
      city: market.location?.city,
      county: market.location?.county,
      tags: market.tags,
      highlights: market.highlights,
    }),
    category: market.categories?.[0],
    tags: market.tags,
    location: market.location,
    vendorType: ['farmers-market'],
  };
}

// Parse JSON safely
function parseJsonSafe<T>(content: string): T | null {
  try {
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
}

// Default sample data for search index (CRA doesn't support import.meta.glob)
const defaultProducts: ProductData[] = [
  { id: 'strawberries', name: 'Strawberries', slug: 'strawberries', category: 'Fruits', description: 'Fresh Florida strawberries', keywords: ['berries', 'fruit', 'sweet'] },
  { id: 'tomatoes', name: 'Tomatoes', slug: 'tomatoes', category: 'Vegetables', description: 'Vine-ripened tomatoes', keywords: ['vegetables', 'produce'] },
  { id: 'mangoes', name: 'Mangoes', slug: 'mangoes', category: 'Fruits', description: 'Tropical Florida mangoes', keywords: ['fruit', 'tropical'] },
  { id: 'citrus', name: 'Citrus', slug: 'citrus', category: 'Fruits', description: 'Fresh oranges, grapefruits, and tangerines', keywords: ['fruit', 'oranges'] },
  { id: 'peppers', name: 'Peppers', slug: 'peppers', category: 'Vegetables', description: 'Bell and hot peppers', keywords: ['vegetables', 'produce'] },
  { id: 'lettuce', name: 'Lettuce', slug: 'lettuce', category: 'Vegetables', description: 'Fresh leafy greens', keywords: ['vegetables', 'greens', 'salad'] },
  { id: 'honey', name: 'Local Honey', slug: 'honey', category: 'Specialty', description: 'Raw local honey from Florida bees', keywords: ['honey', 'bee products', 'local'] },
  { id: 'eggs', name: 'Farm Fresh Eggs', slug: 'eggs', category: 'Dairy & Eggs', description: 'Free-range farm eggs', keywords: ['eggs', 'farm fresh'] },
];

const defaultVendors: VendorData[] = [
  { id: 'fancy-farms', name: 'Fancy Farms', slug: 'fancy-farms', type: 'Farm', description: 'U-Pick strawberry farm', location: { city: 'Fort Myers', county: 'Lee' }, vendorType: ['farm', 'u-pick'] },
  { id: 'pine-island-tropicals', name: 'Pine Island Tropicals', slug: 'pine-island-tropicals', type: 'Farm', description: 'Tropical fruit farm', location: { city: 'Pine Island', county: 'Lee' }, vendorType: ['farm', 'tropical'] },
  { id: 'sun-harvest-citrus', name: 'Sun Harvest Citrus', slug: 'sun-harvest-citrus', type: 'Farm Stand', description: 'Fresh citrus and juices', location: { city: 'Fort Myers', county: 'Lee' }, vendorType: ['farm-stand', 'citrus'] },
];

const defaultMarkets: MarketData[] = [
  { id: 'cape-coral-farmers-market', name: 'Cape Coral Farmers Market', slug: 'cape-coral-farmers-market', description: 'Weekly farmers market in downtown Cape Coral', location: { city: 'Cape Coral', county: 'Lee' }, categories: ['farmers-market'] },
  { id: 'fort-myers-downtown', name: 'Fort Myers Downtown Farmers Market', slug: 'fort-myers-downtown', description: 'Historic downtown Fort Myers market', location: { city: 'Fort Myers', county: 'Lee' }, categories: ['farmers-market'] },
  { id: 'naples-farmers-market', name: 'Naples Farmers Market', slug: 'naples-farmers-market', description: 'Third Street South farmers market', location: { city: 'Naples', county: 'Collier' }, categories: ['farmers-market'] },
  { id: 'bonita-springs-market', name: 'Bonita Springs Farmers Market', slug: 'bonita-springs-market', description: 'Local produce and artisan goods', location: { city: 'Bonita Springs', county: 'Lee' }, categories: ['farmers-market'] },
];

// Load all data - uses static defaults for CRA compatibility
async function loadDataFiles(): Promise<{
  products: ProductData[];
  vendors: VendorData[];
  markets: MarketData[];
}> {
  // Return default sample data (CRA doesn't support import.meta.glob)
  // In production, this could be replaced with API calls or static imports
  return {
    products: defaultProducts,
    vendors: defaultVendors,
    markets: defaultMarkets,
  };
}

// Calculate relevance score for a search match
function calculateScore(query: string, searchableText: string): number {
  if (!query.trim()) return 0;
  
  const normalizedQuery = normalizeText(query);
  const words = normalizedQuery.split(/\s+/);
  let score = 0;
  
  for (const word of words) {
    if (!word) continue;
    
    // Exact word match at start of name
    if (searchableText.startsWith(word)) {
      score += 100;
    }
    
    // Word match in name (beginning)
    const nameIndex = searchableText.indexOf(word);
    if (nameIndex >= 0 && nameIndex < searchableText.indexOf(' ', 50)) {
      score += 50;
    }
    
    // Word appears multiple times
    const regex = new RegExp(word, 'gi');
    const matches = searchableText.match(regex);
    if (matches) {
      score += matches.length * 10;
    }
  }
  
  return score;
}

// Check if entry matches filters
function matchesFilters(entry: SearchIndexEntry, filters: SearchFilters): boolean {
  // Category filter
  if (filters.categories && filters.categories.length > 0) {
    const entryCategory = entry.category?.toLowerCase() || '';
    const matchesCategory = filters.categories.some((cat: string) => 
      entryCategory.includes(cat.toLowerCase()) ||
      entry.tags?.some((tag: string) => tag.toLowerCase().includes(cat.toLowerCase()))
    );
    if (!matchesCategory) return false;
  }

  // Location filter
  if (filters.locations && filters.locations.length > 0) {
    const entryCity = entry.location?.city?.toLowerCase() || '';
    const entryCounty = entry.location?.county?.toLowerCase() || '';
    const matchesLocation = filters.locations.some((loc: string) => 
      entryCity.includes(loc.toLowerCase()) ||
      entryCounty.includes(loc.toLowerCase())
    );
    if (!matchesLocation) return false;
  }

  // Vendor type filter
  if (filters.vendorTypes && filters.vendorTypes.length > 0) {
    if (entry.vendorType) {
      const matchesVendorType = entry.vendorType.some((vt: string) =>
        filters.vendorTypes!.some((ft: string) => vt.toLowerCase().includes(ft.toLowerCase()))
      );
      if (!matchesVendorType) return false;
    } else if (entry.type !== 'product') {
      // Non-product entries without vendor type
      return false;
    }
  }

  // Season filter
  if (filters.seasons && filters.seasons.length > 0) {
    const entryMonths = entry.seasonalMonths || [];
    const matchesSeason = filters.seasons.some((season: string) => {
      const seasonMonths = SEASON_MONTHS[season as keyof typeof SEASON_MONTHS] || [];
      return entryMonths.some((month: string) => seasonMonths.includes(month));
    });
    if (!matchesSeason && entryMonths.length > 0) return false;
  }

  return true;
}

// Search the index
function searchIndexInternal(options: SearchOptions): SearchResult[] {
  const { query, filters, limit = 50 } = options;
  
  let results: SearchResult[] = [];
  
  // If no query, return filtered results
  if (!query.trim()) {
    results = searchIndex
      .filter(entry => !filters || matchesFilters(entry, filters))
      .map((entry) => {
        const seasonalMonths = entry.seasonalMonths || [];

        return {
          type: entry.type,
          id: entry.id,
          name: entry.name,
          slug: entry.slug,
          description: entry.description,
          category: entry.category,
          location: entry.location?.city || entry.location?.county,
          city: entry.location?.city,
          county: entry.location?.county,
          vendorType: entry.vendorType,
          seasonalNote:
            seasonalMonths.length > 0
              ? `Available: ${seasonalMonths.slice(0, 3).join(', ')}${seasonalMonths.length > 3 ? '...' : ''}`
              : undefined,
          score: 0,
          tags: entry.tags,
        };
      })
      .sort((a, b) => {
        // Sort by name
        return a.name.localeCompare(b.name);
      })
      .slice(0, limit);
    
    return results;
  }

  // Search with query
  const normalizedQuery = normalizeText(query);
  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 1);

  for (const entry of searchIndex) {
    // Check filters first
    if (filters && !matchesFilters(entry, filters)) continue;

    // Calculate relevance score
    let score = calculateScore(normalizedQuery, entry.searchableText);
    
    // If fuzzy search, also check partial matches
    if (score === 0 && queryWords.length > 0) {
      for (const word of queryWords) {
        if (word.length < 3) continue;
        if (entry.searchableText.includes(word)) {
          score = 5; // Low score for fuzzy matches
          break;
        }
      }
    }

    // Skip if no match
    if (score === 0) continue;

    // Generate highlights
    const highlights: { field: string; matchedText: string }[] = [];
    for (const word of queryWords) {
      if (word.length < 3) continue;
      const regex = new RegExp(`(${word})`, 'gi');
      if (regex.test(entry.name)) {
        highlights.push({ field: 'name', matchedText: word });
      }
    }

    const seasonalMonths = entry.seasonalMonths || [];

    results.push({
      type: entry.type,
      id: entry.id,
      name: entry.name,
      slug: entry.slug,
      description: entry.description,
      category: entry.category,
      location: entry.location?.city || entry.location?.county,
      city: entry.location?.city,
      county: entry.location?.county,
      vendorType: entry.vendorType,
      seasonalNote:
        seasonalMonths.length > 0
          ? `Available: ${seasonalMonths.slice(0, 3).join(', ')}${seasonalMonths.length > 3 ? '...' : ''}`
          : undefined,
      score,
      highlights,
      tags: entry.tags,
    });
  }

  // Sort by score (descending)
  results.sort((a, b) => b.score - a.score);

  // Apply limit
  return results.slice(0, limit);
}

// Build the search index
export async function buildSearchIndex(): Promise<void> {
  if (isIndexBuilt) return;
  
  // Prevent multiple simultaneous builds
  if (indexPromise) {
    return indexPromise;
  }
  
  indexPromise = (async () => {
    const { products, vendors, markets } = await loadDataFiles();
    
    searchIndex = [];
    
    // Add products
    for (const product of products) {
      searchIndex.push(createProductEntry(product));
    }
    
    // Add vendors
    for (const vendor of vendors) {
      searchIndex.push(createVendorEntry(vendor));
    }
    
    // Add markets
    for (const market of markets) {
      searchIndex.push(createMarketEntry(market));
    }
    
    isIndexBuilt = true;
    console.log(`Search index built with ${searchIndex.length} entries (${products.length} products, ${vendors.length} vendors, ${markets.length} markets)`);
  })();
  
  return indexPromise;
}

// Get the current search index
export function getSearchIndex(): SearchIndexEntry[] {
  return searchIndex;
}

// Search products/vendors/markets
export function searchProducts(query: string, options: Partial<SearchOptions> = {}): SearchResult[] {
  if (!isIndexBuilt) {
    console.warn('Search index not built. Call buildSearchIndex() first.');
    return [];
  }
  
  return searchIndexInternal({
    query,
    limit: options.limit || 50,
    fuzzy: options.fuzzy !== false,
    filters: options.filters,
    offset: options.offset,
  });
}

// Get all unique categories from the index
export function getSearchCategories(): string[] {
  const categories = new Set<string>();
  for (const entry of searchIndex) {
    if (entry.category) {
      categories.add(entry.category);
    }
  }
  return Array.from(categories).sort();
}

// Get all unique locations from the index
export function getSearchLocations(): Array<{ city: string; county?: string }> {
  const locations = new Map<string, { city: string; county?: string }>();
  for (const entry of searchIndex) {
    if (entry.location?.city) {
      const key = entry.location.city.toLowerCase();
      if (!locations.has(key)) {
        locations.set(key, { 
          city: entry.location.city, 
          county: entry.location.county 
        });
      }
    }
  }
  return Array.from(locations.values()).sort((a, b) => a.city.localeCompare(b.city));
}

// Get search suggestions based on partial query
export function getSearchSuggestions(query: string, limit = 5): string[] {
  if (!query.trim() || !isIndexBuilt) return [];
  
  const normalizedQuery = normalizeText(query);
  const suggestions = new Set<string>();
  
  for (const entry of searchIndex) {
    if (suggestions.size >= limit) break;
    
    const nameLower = entry.name.toLowerCase();
    if (nameLower.includes(normalizedQuery) || 
        normalizedQuery.includes(nameLower.split(' ')[0])) {
      suggestions.add(entry.name);
    }
  }
  
  return Array.from(suggestions).slice(0, limit);
}
